import { useState } from 'react';
import { Box, Icon, Link, Text, Title } from '@nimbus-ds/components';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationCircleIcon,
} from '@nimbus-ds/icons';
import tokens from '@nimbus-ds/tokens/dist/js/tokens';
import { useTranslation } from 'react-i18next';
import { SelectionRequest } from '@tiendanube/common';
import Logo from 'App/components/Logo/Logo';
import { Stack, SubmitButton, useResponsive } from 'commons/components';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { TaskCheckboxCard, TaskInputCard, TaskRadioCard } from '..';
import './QuestionnaireForm.scss';

const CATEGORY_ECOMMERCE = 'e-commerce';
const CATEGORY_OFFLINE = 'offline';

/**
 * Checks if category array contains both e-commerce and offline values
 */
function hasEcommerceAndOffline(category: string[] | undefined): boolean {
  return (
    category?.length === 2 &&
    category?.includes(CATEGORY_ECOMMERCE) &&
    category?.includes(CATEGORY_OFFLINE)
  );
}

function filterQuestionnaireOptions(
  questionnaireOptions: QuestionnaireFormProps['questionnaireOptions'],
) {
  return {
    options: questionnaireOptions.filter(
      (option) =>
        !option.code.includes('other') && !option.code.includes('skip'),
    ),
    skipped: questionnaireOptions.find((option) =>
      option.code.includes('skip'),
    ),
    other: questionnaireOptions.find((option) => option.code.includes('other')),
    dontKnow: questionnaireOptions.find((option) =>
      option.code.includes('dont_know'),
    ),
    salesStatusStarting: questionnaireOptions.find((option) =>
      option.code.includes('starting'),
    ),
  };
}

function getQuestionnaireTranslations(
  currentGroup: string,
  category: string[] | undefined,
  hasSelectedEcommerce: boolean | undefined,
  t: ReturnType<typeof useTranslationPreOnboarding>,
) {
  const getTranslationKey = (field: 'title' | 'subtitle') => {
    if (category && category.length > 1) {
      return `questionnaire.${currentGroup}.category.mixed.${field}`;
    }
    if (category?.length) {
      return `questionnaire.${currentGroup}.category.${category[0]}.${field}`;
    }
    return `questionnaire.${currentGroup}.${field}`;
  };

  const getTitleKey = () => {
    const isSalesAmountGroup = currentGroup.startsWith('sales_amount');
    if (isSalesAmountGroup && hasSelectedEcommerce) {
      return `questionnaire.${currentGroup}.title_ecommerce`;
    }
    return getTranslationKey('title');
  };

  const getSubtitleKey = () => {
    if (hasEcommerceAndOffline(category)) {
      return `questionnaire.${currentGroup}.category.mixed.subtitle_free_text`;
    }
    return getTranslationKey('subtitle');
  };

  return {
    title: t(getTitleKey()),
    subtitle: t(getSubtitleKey()),
  };
}

function getFreeTextValue(
  other: { id: number } | undefined,
  values: SelectionRequest[],
): string {
  return other
    ? values.find((item) => item.domain_mapping_id === other.id)?.free_text ??
        ''
    : '';
}

interface QuestionnaireFormProps {
  onSubmit: () => void;
  onReturn?: () => void;
  values: SelectionRequest[];
  setValues: (v: SelectionRequest[]) => void;
  currentGroup: string;
  isCheckbox: boolean;
  questionnaireOptions: {
    id: number;
    code: string;
    label: string;
    description?: string;
    category?: string;
  }[];
  onSkipGroup?: (groupKey: string, skipId: number) => void;
  isLastStep?: boolean;
  isQuestionnaireRevamp?: boolean;
  category?: string[];
  hasSelectedEcommerce?: boolean;
}

export function QuestionnaireForm({
  onSubmit,
  onReturn,
  values,
  setValues,
  currentGroup,
  isCheckbox,
  questionnaireOptions,
  onSkipGroup,
  isLastStep,
  isQuestionnaireRevamp,
  category,
  hasSelectedEcommerce,
}: Readonly<QuestionnaireFormProps>) {
  const t = useTranslationPreOnboarding();
  const { isMobile } = useResponsive();
  const {
    i18n: { language },
  } = useTranslation();
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [showError, setShowError] = useState(false);
  const { options, skipped, other, dontKnow, salesStatusStarting } =
    filterQuestionnaireOptions(questionnaireOptions);
  const hasSkippedSelection =
    skipped !== undefined &&
    values.some((selection) => selection.domain_mapping_id === skipped.id);
  const shouldShowValidationError = values.length === 0 || hasSkippedSelection;

  const handleChangeTask = (id: number, checked: boolean) => {
    setShowError(false);
    if (checked) {
      let updated: SelectionRequest[];

      if (isCheckbox) {
        const isDontKnow = dontKnow?.id === id;
        const isJustStarting = salesStatusStarting?.id === id;

        if (isDontKnow || isJustStarting) {
          updated = [{ domain_mapping_id: id }];
        } else {
          updated = [...values, { domain_mapping_id: id }];
          updated = updated.filter(
            (v) =>
              v.domain_mapping_id !== skipped?.id &&
              v.domain_mapping_id !== dontKnow?.id &&
              v.domain_mapping_id !== salesStatusStarting?.id,
          );
        }
      } else {
        updated = [{ domain_mapping_id: id }];
      }

      setValues(updated);
    } else if (isCheckbox) {
      setValues(values.filter((v) => v.domain_mapping_id !== id));
    }
  };

  const handleSkip = () => {
    setShowError(false);
    if (skipped && onSkipGroup) {
      onSkipGroup(currentGroup, skipped.id);
    }
  };

  const customOnSubmit = () => {
    if (shouldShowValidationError) {
      setShowError(true);
      return;
    }
    setIsLoadingPage(true);
    onSubmit();
  };

  const handleFreeTextChange = (id: number) => (newFreeText: string) => {
    const onlyWhitespace = newFreeText.trim() === '';

    const exists = values.find((item) => item.domain_mapping_id === id);
    let newValues = [...values];

    if (exists) {
      if (!onlyWhitespace) {
        newValues = newValues.map((item) =>
          item.domain_mapping_id === id
            ? { ...item, free_text: newFreeText }
            : item,
        );
      } else {
        newValues = newValues.filter((item) => item.domain_mapping_id !== id);
      }
    } else if (!onlyWhitespace) {
      newValues.push({ domain_mapping_id: id, free_text: newFreeText });
    }
    if (dontKnow && id !== dontKnow.id) {
      newValues = newValues.filter((v) => v.domain_mapping_id !== dontKnow?.id);
    }

    // Clear error when user types in the "other" field
    if (!onlyWhitespace) {
      setShowError(false);
    }

    setValues(newValues);
  };

  const freeText = getFreeTextValue(other, values);
  const { title, subtitle } = getQuestionnaireTranslations(
    currentGroup,
    category,
    hasSelectedEcommerce,
    t,
  );
  const optionsShown = other ? options.length + 1 : options.length;
  const otherOnSameGrid = optionsShown > 5 && options.length % 2 === 1;
  const isOnlyEcommerce =
    category?.length === 1 && category[0] === CATEGORY_ECOMMERCE;
  const shouldRemovePlaceholder =
    isOnlyEcommerce || hasEcommerceAndOffline(category);

  return (
    <Box
      flex="1"
      padding={isMobile ? '8' : '16'}
      display="flex"
      flexDirection="column"
      height="100%"
      data-style="stratus--questionnaire-insti"
      data-revamp={isQuestionnaireRevamp ? 'true' : undefined}
    >
      <Box>
        <Logo
          src={language === 'pt-BR' ? 'Nuvemshop' : 'Tiendanube'}
          height={24}
          color={
            isQuestionnaireRevamp
              ? tokens.color.dark.neutral['text-high'].value
              : tokens.color.light.primary.interactive.value
          }
        />
        <Box
          marginBottom="6"
          marginTop="6"
          display="flex"
          flexDirection="column"
          gap="1"
        >
          <Title as="h2" color="primary-textHigh">
            {title}
          </Title>
          <Title as="h6" color="primary-textHigh" fontWeight="regular">
            {subtitle}
          </Title>
        </Box>

        <Box
          display="grid"
          gap="3"
          gridTemplateColumns={optionsShown > 5 ? '1fr 1fr' : '1fr'}
        >
          {isCheckbox
            ? options.map((opt) => (
                <TaskCheckboxCard
                  key={opt.id}
                  task={opt}
                  checked={values.some(
                    (selection) => selection.domain_mapping_id === opt.id,
                  )}
                  onChange={handleChangeTask}
                  hasError={showError}
                  isRevamp={isQuestionnaireRevamp}
                />
              ))
            : options.map((opt) => (
                <TaskRadioCard
                  key={opt.id}
                  task={opt}
                  selectedId={values[0]?.domain_mapping_id ?? null}
                  onChange={(id) => handleChangeTask(id, true)}
                  name={t(`questionnaire.${currentGroup}.title`)}
                  hasError={showError}
                  isRevamp={isQuestionnaireRevamp}
                />
              ))}
          {other && otherOnSameGrid && (
            <TaskInputCard
              freeText={freeText}
              onChange={handleFreeTextChange(other.id)}
              label={t(`questionnaire.${currentGroup}.other`)}
              placeholder={t(`questionnaire.which`)}
              hasError={showError}
              isRevamp={isQuestionnaireRevamp}
            />
          )}
        </Box>
        {other && !otherOnSameGrid && (
          <Box paddingTop={options.length === 0 ? 'none' : '3'}>
            <TaskInputCard
              freeText={freeText}
              onChange={handleFreeTextChange(other.id)}
              label={
                shouldRemovePlaceholder
                  ? ''
                  : t(`questionnaire.${currentGroup}.other`)
              }
              placeholder={
                shouldRemovePlaceholder ? '' : t('questionnaire.which')
              }
              hasError={showError}
              isRevamp={isQuestionnaireRevamp}
            />
          </Box>
        )}
        {showError && (
          <Box
            paddingTop="3"
            display="flex"
            gap="1"
            alignItems="center"
            data-style="stratus--questionnaire-error"
          >
            <Icon
              color="danger-textLow"
              source={<ExclamationCircleIcon size={16} />}
            />
            <Text color="danger-textLow" fontSize="caption">
              {shouldRemovePlaceholder
                ? t('questionnaire.validation.complete_at_least_one')
                : t('questionnaire.validation.select_at_least_one')}
            </Text>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop="auto"
        paddingTop="6"
      >
        <Stack gap="3">
          {onReturn && (
            <SubmitButton
              onClick={() => {
                setShowError(false);
                onReturn();
              }}
              disabled={isLoadingPage}
              data-style="stratus--questionnaire-insti-button-secondary"
              data-variant={isMobile ? 'icon-only' : undefined}
            >
              {isMobile ? (
                <Icon color="currentColor" source={<ArrowLeftIcon />} />
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="2"
                >
                  <Icon color="currentColor" source={<ArrowLeftIcon />} />
                  {t(`questionnaire.return`)}
                </Box>
              )}
            </SubmitButton>
          )}
          <SubmitButton
            onClick={
              isLastStep
                ? customOnSubmit
                : () => {
                    if (shouldShowValidationError) {
                      setShowError(true);
                      return;
                    }
                    onSubmit();
                  }
            }
            isLoading={isLoadingPage}
            appearance="primary"
            data-style="stratus--questionnaire-insti-button"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="2"
            >
              {isLastStep
                ? t(`questionnaire.submit`)
                : t(`questionnaire.continue`)}
              <Icon color="currentColor" source={<ArrowRightIcon />} />
            </Box>
          </SubmitButton>
        </Stack>
        {skipped && (
          <Link
            as="button"
            textDecoration="none"
            onClick={handleSkip}
            appearance="primary"
            data-style="stratus--questionnaire-insti-button-skip"
          >
            {t(`questionnaire.skip`)}
          </Link>
        )}
      </Box>
    </Box>
  );
}
